// src/utils/images.js
import { FALLBACK_IMG } from "../constants/images";

/**
 * Domains that commonly break in production (DNS blocked / adblock / placeholders).
 * Add/remove based on your environment.
 */
const BLOCKED_HOSTS = new Set([
  "via.placeholder.com",
  "placehold.co",
  "placeholder.com",
]);

/**
 * Returns true if URL is clearly unusable for your app.
 */
export function isBadImageUrl(url) {
  if (!url) return true;
  const s = String(url).trim();
  if (!s) return true;

  // Disallow placeholder domains
  try {
    const u = new URL(s, window.location.origin);
    if (BLOCKED_HOSTS.has(u.hostname)) return true;
  } catch {
    // If URL constructor fails, still allow data: and relative paths
    if (!s.startsWith("data:") && !s.startsWith("/") && !s.startsWith("http")) return true;
  }

  // Disallow obvious broken patterns
  if (s.includes("undefined") || s.includes("null")) return true;

  return false;
}

/**
 * Coerce "any trade image field shape" into an ordered list of candidate URLs.
 */
export function extractImageCandidates(trade) {
  const raw =
    trade?.imageURLs ??
    trade?.imageUrls ??
    trade?.images ??
    trade?.imageURL ??
    trade?.imageUrl ??
    trade?.photoURL ??
    trade?.photoUrl ??
    null;

  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string") return [raw];
  return [];
}

/**
 * Pick a safe image URL for a trade.
 * If nothing valid, return FALLBACK_IMG.
 */
export function getTradeImageSrc(trade) {
  const candidates = extractImageCandidates(trade);

  // First valid, non-blocked candidate
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (!isBadImageUrl(s)) return s;
  }

  return FALLBACK_IMG;
}

/**
 * A safe <img onError> handler that swaps to fallback exactly once.
 */
export function handleImgError(e) {
  const img = e?.currentTarget;
  if (!img) return;

  // Avoid loops
  if (img.dataset?.fallbackApplied === "1") return;

  img.dataset.fallbackApplied = "1";
  img.src = FALLBACK_IMG;
}
